import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Input, Button, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { setEmployeeDetail } from "../../state/slices/accountDetailSlice";
import "../../styles/components/profileView/profileCard.scss";

const PLACEHOLDER =
  "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541";

const ProfileCard = ({ name, image }) => {
  const dispatch = useDispatch();
  const { details } = useSelector((state) => state.accountDetailSlice);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [form] = Form.useForm();

  const baseUrl =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ?? "http://localhost:8000";

  const openModal = () => {
    form.setFieldsValue({
      first_name: details?.first_name ?? "",
      middle_name: details?.middle_name ?? "",
      last_name: details?.last_name ?? "",
    });
    setPreviewUrl(null);
    setSelectedFile(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setPreviewUrl(null);
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const { first_name, middle_name, last_name } = form.getFieldsValue();
    const formData = new FormData();
    formData.append("first_name", first_name);
    formData.append("middle_name", middle_name ?? "");
    formData.append("last_name", last_name);
    if (selectedFile) {
      formData.append("photo", selectedFile);
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("api_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/profile/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      dispatch(setEmployeeDetail(res.data.details));
      message.success("Profile updated.");
      closeModal();
    } catch (err) {
      message.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // profile_photo is either a full external URL (Google avatar) or a
  // site-relative upload path (/uploads/xxx.jpg) — only the latter needs
  // the API base URL prefixed.
  const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value);

  // Bust cache after an upload by appending a timestamp stored in details
  const currentImage = details?.profile_photo
    ? (() => {
        const url = isAbsoluteUrl(details.profile_photo)
          ? details.profile_photo
          : `${baseUrl}${details.profile_photo}`;
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}t=${details?.updated_at ?? Date.now()}`;
      })()
    : null;

  const handleImageError = (event) => {
    if (event.target.src !== PLACEHOLDER) {
      event.target.src = PLACEHOLDER;
    }
  };

  return (
    <div className="main-content">
      <div className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1>Welcome, {name || "User"}!</h1>
            <p>
              This is PIVOT, your personalized UPOU dashboard. Here you can
              quickly access UPOU's systems, stay up-to-date with UPOU news and
              events through the bulletin, and find other relevant information.
            </p>
          </div>
          <div className="profile-section">
            <div className="profile-picture-wrapper" onClick={openModal}>
              <img
                src={currentImage || image || PLACEHOLDER}
                alt="Profile"
                className="profile-img"
                onError={handleImageError}
              />
              <div className="profile-edit-overlay">
                <EditOutlined className="profile-edit-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Edit Profile"
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnClose
      >
        <div className="profile-modal-photo-row">
          <div
            className="profile-modal-avatar-wrapper"
            onClick={() => fileInputRef.current?.click()}
          >
            <img
              src={previewUrl || currentImage || image || PLACEHOLDER}
              alt="Preview"
              className="profile-modal-avatar"
              onError={handleImageError}
            />
            <div className="profile-modal-avatar-overlay">
              <EditOutlined />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <span className="profile-modal-photo-hint">Click photo to change</span>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label="First Name"
            name="first_name"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item label="Middle Name" name="middle_name">
            <Input placeholder="Optional" disabled />
          </Form.Item>
          <Form.Item
            label="Last Name"
            name="last_name"
            rules={[{ required: true, message: "Last name is required" }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item label="Email">
            <Input value={details?.email ?? ""} disabled />
          </Form.Item>
        </Form>

        <div className="profile-modal-footer">
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileCard;
